import { useCallback, useEffect, useState } from 'react'
import { PARTIAL_NOTE, WITHHELD, partialOf, sumShown } from '../api/answer'
import { Withheld, markPartial, memberName, memberText } from './Withheld'
import type { Api } from '../api'
import { ApiError, USD } from '../api/types'
import type { ApiFailure, Currency, DecomposeLevel, DecomposePath, DecomposeResponse } from '../api/types'
import { EmptyState, Loading, money, currencyNote } from './ui'
import { Explain, useExplain } from './DevModeContext'
import { FailureState } from './FailureState'
import { ExportButtons } from './ExportButtons'
import type { ExportDoc, ExportRow } from '../export/model'

/**
 * TREE VIEW — the Fabric report's decomposition tree.
 *
 * Analyses Amount Remaining, broken down by whichever field the reader picks at
 * each step. That choice is the difference from the Aging Dashboard: the matrix
 * walks a FIXED hierarchy, this one lets you ask "what is the biggest part of
 * this, by customer?" and then "and within that, by team?".
 *
 * ---------------------------------------------------------------------------
 * THE MENU COMES FROM THE SERVER
 *
 * `availableFields` is computed in the database, because only the database
 * knows which columns this caller may see. A reader without SLS_Mod is never
 * offered "by salesperson" — not offered-and-refused, which would still confirm
 * the field exists. This component renders the menu it is handed and has no
 * opinion about which fields should be in it.
 *
 * ---------------------------------------------------------------------------
 * WHY BARS
 *
 * A decomposition is read by comparing magnitudes, so each node is drawn as a
 * proportional bar against the largest in its level. The document COUNT sits
 * beside the amount because 4M across one document is a different problem from
 * 4M across two hundred, and the bar alone cannot say which.
 */

const FIELD_LABEL: Record<DecomposeLevel, string> = {
  BusinessUnit: 'Business Unit',
  BusinessTeam: 'Business Team',
  Salesperson: 'Salesperson',
  Customer: 'Customer',
  Document: 'Document',
}

/** One step the reader has taken: a field, the value chosen, and what it was worth. */
interface Step {
  field: DecomposeLevel
  value: string
  amount: number | null
}

/* A code on the level total only when every bar on it agrees. Mixed bars sum
   to a figure with no unit, and this line is the one people read as "the
   total" — so it is the worst place to guess. */
function levelCodeOf(nodes: { currencyCode: string | null }[]): string | null {
  const codes = new Set(nodes.map((n) => n.currencyCode))
  return codes.size === 1 ? [...codes][0] : null
}

function pathFrom(steps: Step[]): DecomposePath {
  const p: DecomposePath = {}
  for (const s of steps) {
    if (s.field === 'BusinessUnit') p.bu = s.value
    else if (s.field === 'BusinessTeam') p.bt = s.value
    else if (s.field === 'Salesperson') p.sp = s.value
    else if (s.field === 'Customer') p.customer = s.value
    else p.document = s.value
  }
  return p
}

export function DecompositionTree({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
}) {
  const [steps, setSteps] = useState<Step[]>([])
  const [groupBy, setGroupBy] = useState<DecomposeLevel>('BusinessUnit')
  const [data, setData] = useState<DecomposeResponse | null>(null)
  /** The new permission model answered (M8): a blank amount is withheld, and Partial marks a partly withheld bar. */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const { data: result, vis } = await api.receivablesDecompose({
        groupBy,
        ...pathFrom(steps),
        currency,
      })
      setData(result)
      setV2(vis !== null)
      // The server may have overridden the choice — a caller who asks to break
      // down by a column they cannot see is quietly given BusinessUnit instead.
      // Follow what came back rather than what was asked for.
      if (result.groupedBy && result.groupedBy !== groupBy) setGroupBy(result.groupedBy)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, groupBy, steps, currency, viewAs])

  useEffect(() => {
    void load()
  }, [load])

  // Looking through different eyes is a different book. Start again at the root
  // rather than keeping a path built from somebody else's data.
  useEffect(() => {
    setSteps([])
    setGroupBy('BusinessUnit')
  }, [viewAs])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />

  const nodes = data?.nodes ?? []
  const largest = Math.max(1, ...nodes.map((n) => Math.abs(n.amount ?? 0)))
  // Under the new model a bar can be withheld while its neighbours show: the
  // level total is the sum of what shows, marked when it leaves anything out (M8).
  const shownTotal = v2 ? sumShown(nodes, ['amount', (n) => n.amount]) : null
  const levelTotal = shownTotal ? (shownTotal.value ?? 0) : nodes.reduce((s, n) => s + (n.amount ?? 0), 0)
  const levelTotalText = shownTotal && shownTotal.value === null
    ? WITHHELD
    : `${money(levelTotal, currency, levelCodeOf(nodes))}${shownTotal?.partial ? '*' : ''}`

  /** Fields still worth offering — the ones already used are spent. */
  const used = new Set(steps.map((s) => s.field))
  const offer = (data?.availableFields ?? []).filter((f) => !used.has(f))

  /* THE EXTRACT — this level's bars as a table: share of the level total, the
     amount, the document count, the path stated in full. */
  const buildDoc = (): ExportDoc | null => {
    if (loading || nodes.length === 0) return null
    const pathLine =
      steps.length > 0
        ? [`Path: ${steps.map((s) => `${FIELD_LABEL[s.field]}: ${s.value || '(blank)'}`).join('  →  ')}`]
        : []
    return {
      report: 'Receivables',
      page: 'Tree View',
      title: 'Decomposition by amount remaining',
      subtitle: `Broken down by ${FIELD_LABEL[groupBy].toLowerCase()} — ${nodes.length} bar(s), level total ${levelTotalText}.`,
      context: [
        `Analysing: Amount remaining · broken down by ${FIELD_LABEL[groupBy]}`,
        ...pathLine,
        `Currency: ${currencyNote(currency)}`,
      ],
      privileged: viewAs,
      currency,
      tables: [
        {
          columns: [
            { header: FIELD_LABEL[groupBy] },
            { header: 'Share', kind: 'pct' },
            { header: 'Amount Remaining', kind: 'money' },
            { header: 'Documents', kind: 'int' },
          ],
          rows: nodes.map<ExportRow>((n) => ({
            cells: [
              memberText(n.nodeKey, partialOf(n).has('nodeKey'), FIELD_LABEL[groupBy]),
              levelTotal !== 0 && (n.amount !== null || !v2) ? (n.amount ?? 0) / levelTotal : null,
              n.amount === null && v2 ? WITHHELD : n.amount,
              n.docCount,
            ],
          })),
          note: `Showing the largest ${nodes.length} by amount remaining, as drawn on screen. Share is of the level total.${
            nodes.length >= 20 ? ' Smaller entries beyond the top 20 are not drawn.' : ''
          }${v2 && (shownTotal?.partial || nodes.some((n) => partialOf(n).size > 0)) ? ` ${PARTIAL_NOTE}` : ''}`,
        },
      ],
    }
  }

  return (
    // The path and the field picker stay put; the bars scroll under them.
    <div className="scroll-slim min-h-0 flex-1 overflow-y-auto p-5">
      {/* ---------------- Path ---------------- */}
      <nav aria-label="Decomposition path" className="mb-5 flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setSteps([])}
          className={`rounded px-2.5 py-1 text-(length:--text-small) font-medium transition-colors ${
            steps.length === 0
              ? 'bg-(--color-navy-700) text-white'
              : 'bg-(--color-surface-3) text-(--color-ink-2) hover:bg-(--color-navy-100)'
          }`}
        >
          All receivables
        </button>

        {/* Index in the key: the step stack is append/truncate-only so position
            is stable, and without it two steps carrying the same (field, value)
            collide — duplicate sibling keys corrupt reconciliation. */}
        {steps.map((s, i) => (
          <span key={`${i}-${s.field}-${s.value}`} className="flex items-center gap-1.5">
            <span aria-hidden className="text-(--color-ink-4)">›</span>
            <button
              type="button"
              onClick={() => setSteps(steps.slice(0, i + 1))}
              title={`${FIELD_LABEL[s.field]}: ${s.value}`}
              className={`max-w-[220px] truncate rounded px-2.5 py-1 text-(length:--text-small) font-medium transition-colors ${
                i === steps.length - 1
                  ? 'bg-(--color-navy-700) text-white'
                  : 'bg-(--color-surface-3) text-(--color-ink-2) hover:bg-(--color-navy-100)'
              }`}
            >
              {s.value || '(blank)'}
            </button>
          </span>
        ))}
      </nav>

      {/* ---------------- Breakdown chooser ---------------- */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
        <Explain surface="filters" measureKey="breakdownBy" className="eyebrow">
          Break down by
        </Explain>
        {offer.length === 0 && (
          <span className="text-(length:--text-small) text-(--color-ink-3)">
            Nothing further — every available field is already on the path.
          </span>
        )}
        {offer.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setGroupBy(f)}
            aria-pressed={groupBy === f}
            className={`rounded-full border px-3 py-1 text-(length:--text-small) font-medium transition-colors ${
              groupBy === f
                ? 'border-(--color-navy-700) bg-(--color-navy-700) text-white'
                : 'border-(--color-line-strong) bg-(--color-surface) text-(--color-ink-2) hover:border-(--color-navy-400) hover:text-(--color-navy-700)'
            }`}
          >
            {FIELD_LABEL[f]}
          </button>
        ))}
        </div>
        <ExportButtons build={buildDoc} />
      </div>

      {/* ---------------- Bars ---------------- */}
      {/* On EVERY fetch. Changing the breakdown field, or clicking a bar to go
          deeper, replaces every row here — and a fade did not say so. See
          TrendChart for the reasoning. */}
      {loading && <Loading label="Loading breakdown…" />}

      {!data ? null : nodes.length === 0 ? (
        <EmptyState
          title={viewAs ? `${viewAs} has nothing here` : 'Nothing to break down'}
          detail="This branch has no open receivables. That is an empty result, not a refusal — a refusal would say so and give you a reference."
        />
      ) : (
        <div>
          {/* A tree row IS a button, so the three figures it carries have nowhere
              to hang an explanation. This strip is the handle, and it renders as
              nothing at all unless the reader has developer mode on. */}
          <TreeLegend />
          <ol className="space-y-0.5">
            {nodes.map((n) => {
              const amount = n.amount ?? 0
              const share = levelTotal !== 0 ? (amount / levelTotal) * 100 : 0
              const width = (Math.abs(amount) / largest) * 100
              // A blank node key cannot be filtered on: the client drops an
              // empty value and the API trims it to NULL, so the "child" query
              // equals this level's and the same bars come back — the path grows
              // while nothing narrows. Refuse the descent, as the Sales
              // decomposition already does. Blank org values are present in the
              // live data (8 blank BU rows in D365_Salesperson_DSO), so a blank
              // bar is drawn in practice.
              // `?.trim()`: the API normalises with IsNullOrWhiteSpace, so a
              // whitespace-only key is truthy here but arrives NULL server-side.
              // `|| loading` matters because the bars stay painted during a child
              // fetch (the loader renders beside them, not instead of them), and
              // groupBy has ALREADY advanced by then. A second click in that
              // window would file a step against the field being requested rather
              // than the one the bars belong to.
              const canDrill =
                !loading &&
                !!n.nodeKey?.trim() &&
                (offer.length > 1 || (offer.length === 1 && offer[0] !== groupBy))

              return (
                <li key={n.nodeKey ?? '(blank)'}>
                  <button
                    type="button"
                    disabled={!canDrill}
                    onClick={() => {
                      // Descend, and ADVANCE the grouping to the next field the
                      // path has not spent. Staying on the field just consumed
                      // draws a single bar equal to the step itself — a dead end
                      // the reader can re-drill forever while nothing narrows.
                      // The Sales tree already does exactly this.
                      // The field the RENDERED bars belong to, taken from the
                      // response — NOT `groupBy`, which may already have advanced
                      // to the field being requested. Filing the step under the
                      // wrong field would mislabel the breadcrumb and spend the
                      // wrong dimension.
                      const barField = data?.groupedBy ?? groupBy
                      const nextSteps = [...steps, { field: barField, value: n.nodeKey ?? '', amount: n.amount }]
                      const spentNext = new Set(nextSteps.map((s) => s.field))
                      const next = (data?.availableFields ?? []).find((f) => !spentNext.has(f))
                      setSteps(nextSteps)
                      if (next) setGroupBy(next)
                    }}
                    className="group relative flex w-full items-center gap-4 rounded px-3 py-2 text-left @max-3xl:gap-2 @max-3xl:px-2 transition-colors hover:bg-(--color-navy-50) disabled:cursor-default disabled:hover:bg-transparent"
                  >
                    {/* The bar sits BEHIND the label rather than beside it, so a
                        long customer name does not squeeze the graphic that the
                        whole visual exists to show. */}
                    <span
                      aria-hidden
                      style={{ width: `${width}%` }}
                      className={`absolute inset-y-1 left-0 rounded-sm transition-all duration-300 ${
                        amount < 0 ? 'bg-(--color-overdue)/12' : 'bg-(--color-navy-500)/14'
                      }`}
                    />
                    <span className="relative min-w-0 flex-1 truncate font-medium text-(--color-ink)">
                      {memberName(n.nodeKey, partialOf(n).has('nodeKey'), FIELD_LABEL[data?.groupedBy ?? groupBy])}
                    </span>
                    <span className="relative shrink-0 text-(length:--text-small) text-(--color-ink-4)">
                      {money(n.docCount)} doc{n.docCount === 1 ? '' : 's'}
                    </span>
                    <span className="relative w-16 shrink-0 text-right text-(length:--text-small) text-(--color-ink-3) @max-3xl:w-11">
                      {v2 && n.amount === null ? '' : `${share.toFixed(1)}%`}
                    </span>
                    <span
                      className={`num relative w-40 shrink-0 font-semibold @max-3xl:w-auto ${
                        amount < 0 ? 'text-(--color-overdue)' : 'text-(--color-ink)'
                      }`}
                    >
                      {n.amount === null ? (v2 ? <Withheld /> : '—') : markPartial(partialOf(n).has('amount'), money(n.amount, currency, n.currencyCode))}
                    </span>
                  </button>
                </li>
              )
            })}
          </ol>

          <p className="mt-4 border-t border-(--color-line) pt-3 text-(length:--text-small) text-(--color-ink-3)">
            Showing the largest {nodes.length} by amount remaining, totalling{' '}
            <span className="num font-semibold text-(--color-ink-2)">{levelTotalText}</span>.
            {nodes.length >= 20 && ' Smaller entries beyond the top 20 are not drawn.'}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * The tree's figures, as a row of things that can be asked about.
 *
 * It renders NOTHING unless the reader has developer mode on and these keys are
 * documented, so the strip is absent rather than empty for the three hundred
 * people who open this report to read it. It exists because a tree row is
 * itself a button — the bars have nowhere to hang an explanation, and a
 * documented figure nobody can reach is the failure the manifest exists to
 * prevent.
 */
function TreeLegend() {
  const { can } = useExplain()
  const keys: [string, string][] = [
    ['amount', 'Amount Remaining'],
    ['share', 'Share'],
    ['docs', 'Documents'],
  ]
  const shown = keys.filter(([k]) => can('tree', k))
  if (shown.length === 0) return null
  return (
    <p className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-(length:--text-small) text-(--color-ink-3)">
      <span className="eyebrow">The figures on each bar</span>
      {shown.map(([k, label]) => (
        <Explain key={k} surface="tree" measureKey={k}>
          {label}
        </Explain>
      ))}
    </p>
  )
}
